import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MAX_SAVED_FILTERS, type PreferencesView } from '@vigeo/shared';
import {
  createSavedFilter, deleteSavedFilter, deleteUnreadableSavedFilters, fetchPreferences, renameSavedFilter, setLastFilters,
  type PreferencesError,
} from '@/lib/api';

/**
 * The signed-in user's remembered and saved roster filters (ADR-019, #476).
 *
 * ONLY FOR AN ADMIN. With `enabled` false there is no query and no function here makes a request:
 * a Viewer's roster never meets a 403 and keeps nothing (ADR-019 §3a). The page passes `isAdmin`.
 */
export const PREFERENCES_KEY = ['preferences'] as const;

/** What a saved-filter action answers when the user can be told in a sentence. */
export const CONFLICT_MESSAGE = 'Your saved filters changed on another computer. They are up to date now; try again.';
export const LIMIT_MESSAGE = `You have ${MAX_SAVED_FILTERS} saved filters. Remove one to save another.`;
export const NAME_TAKEN_MESSAGE = 'You already have a saved filter with that name.';

export const preferencesCode = (error: unknown): string | null => (error as Partial<PreferencesError> | null)?.code ?? null;

export function useRosterPreferences(enabled: boolean) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: fetchPreferences,
    enabled,
    retry: false,
    // Applied once, on open. A refetch on focus would be a second "open" the screen does not act on.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const keep = (view: PreferencesView): PreferencesView => {
    queryClient.setQueryData(PREFERENCES_KEY, view);
    return view;
  };
  /** After a 409 `conflict` the stored list moved on: read it again so what is shown is what is kept. */
  const reload = async () => {
    try {
      keep(await fetchPreferences());
    } catch {
      /* the caller already has a message; a failed reload leaves what was shown */
    }
  };
  return {
    view: query.data,
    loaded: query.isSuccess,
    failed: query.isError,
    reload,
    setLast: async (filters: Parameters<typeof setLastFilters>[0]) => keep(await setLastFilters(filters)),
    create: async (input: Parameters<typeof createSavedFilter>[0]) => keep((await createSavedFilter(input)).view),
    rename: async (id: string, name: string) => keep(await renameSavedFilter(id, name)),
    remove: async (id: string) => keep(await deleteSavedFilter(id)),
    removeUnreadable: async () => keep(await deleteUnreadableSavedFilters()),
  };
}

export type RosterPreferences = ReturnType<typeof useRosterPreferences>;
